import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Fab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
  Backdrop,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format, addDays, subDays, startOfDay, isToday, parse, setHours, setMinutes, isWithinInterval } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import MeetingCard from './MeetingCard';
import BookMeetingDialog from './BookMeetingDialog';
import MeetingDetailsDialog from './MeetingDetailsDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import { calendarService } from '../../services/calendarService';
import { businessConfigService } from '../../services/businessConfigService';
import { isSlotBookable, getSlotTooltip } from '../../utils/slotValidation';
import { findFirstAvailableSlot } from '../../utils/findFirstAvailableSlot';

/**
 * Daily Calendar - Single day view optimized for mobile with time grid
 */
const DailyCalendar = ({ customerId, selectedCalendar }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'pl' ? pl : enUS;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [calendarConfig, setCalendarConfig] = useState(null);
  const [activeCalendar, setActiveCalendar] = useState(selectedCalendar || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        const calendarList = await businessConfigService.getBusinessCalendars(customerId);
        console.log('[DailyCalendar] Fetched calendar list:', calendarList);
        setCalendars(calendarList);
        // Auto-select first calendar if none selected
        if (!activeCalendar && calendarList.length > 0) {
          console.log('[DailyCalendar] Auto-selecting first calendar:', calendarList[0].name);
          setActiveCalendar(calendarList[0].name);
        }
      } catch (err) {
        console.error('Error fetching calendars:', err);
      }
    };

    if (customerId) {
      fetchCalendars();
    }
  }, [customerId]);

  // Fetch calendar config when activeCalendar changes
  useEffect(() => {
    const fetchCalendarConfig = async () => {
      if (activeCalendar) {
        try {
          const calendars = await businessConfigService.getBusinessCalendars(customerId);
          const calendar = calendars.find(cal => cal.name === activeCalendar);
          setCalendarConfig(calendar || {});
          
          // Find and navigate to first available slot ONLY on initial load
          if (!initializedRef.current) {
            const firstAvailableDate = findFirstAvailableSlot(calendar);
            setCurrentDate(firstAvailableDate);
            initializedRef.current = true;
          }
        } catch (err) {
          console.error('Error fetching calendar config:', err);
          setCalendarConfig({});
        }
      } else {
        setCalendarConfig({});
      }
    };

    if (customerId) {
      fetchCalendarConfig();
    }
  }, [customerId, activeCalendar]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const customersData = await calendarService.getDayEvents(customerId, currentDate);
        
        console.log('[DailyCalendar] fetchEvents - customersData:', customersData);
        console.log('[DailyCalendar] fetchEvents - activeCalendar:', activeCalendar);
        
        // Parse events from nested structure: {customerId: {business_name, calendars: {calendarName: {data}}}}
        const allEvents = [];
        Object.entries(customersData).forEach(([custId, customerInfo]) => {
          const calendars = customerInfo.calendars || {};
          console.log('[DailyCalendar] Processing customer:', custId, 'calendars:', Object.keys(calendars));
          
          Object.entries(calendars).forEach(([calendarName, data]) => {
            console.log('[DailyCalendar] Processing calendar:', calendarName, 'events:', data.calendar_events?.length);
            if (activeCalendar === calendarName) {
              const calendarEvents = data.calendar_events || [];
              calendarEvents.forEach(event => {
                const parsedEvent = calendarService.parseEvent(event);
                if (parsedEvent) {
                  allEvents.push({
                    ...parsedEvent,
                    calendarName,
                    customerId: custId,
                    businessName: customerInfo.business_name,
                  });
                } else {
                  console.warn('[DailyCalendar] Failed to parse event:', event);
                }
              });
            } else {
              console.log('[DailyCalendar] Skipping calendar', calendarName, '(active:', activeCalendar, ')');
            }
          });
        });

        console.log('[DailyCalendar] Total parsed events:', allEvents.length);
        // Sort by start time
        allEvents.sort((a, b) => a.start - b.start);
        setEvents(allEvents);
        setError(null);
      } catch (err) {
        console.error('[DailyCalendar] Error fetching events:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch events if we have both customerId, activeCalendar AND calendarConfig
    // This prevents a race condition where events are filtered out before config loads
    if (customerId && activeCalendar && calendarConfig) {
      console.log('[DailyCalendar] Fetching events - customerId:', customerId, 'activeCalendar:', activeCalendar, 'date:', currentDate);
      fetchEvents();
    } else {
      console.log('[DailyCalendar] Skipping fetch - missing:', {
        customerId: !!customerId,
        activeCalendar: !!activeCalendar,
        calendarConfig: !!calendarConfig
      });
    }
  }, [customerId, currentDate, activeCalendar, calendarConfig]);

  const handlePreviousDay = () => {
    setCurrentDate(subDays(currentDate, 1));
  };

  const handleNextDay = () => {
    setCurrentDate(addDays(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleMeetingClick = (meeting) => {
    setSelectedMeeting(meeting);
    setShowDetailsDialog(true);
  };

  const handleAddMeeting = () => {
    setShowBookDialog(true);
  };

  // Generate time slots based on calendar granularity
  const generateTimeSlots = () => {
    const availabilityRules = calendarConfig?.business_hours?.availability_rules;
    if (!calendarConfig || !availabilityRules) {
      // Default to 8 AM - 6 PM with configured granularity
      const granularity = parseInt(calendarConfig?.general?.granularity) || 30;
      const slots = [];
      for (let hour = 8; hour < 18; hour++) {
        for (let min = 0; min < 60; min += granularity) {
          slots.push(`${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
        }
      }
      return slots;
    }

    const dayOfWeek = currentDate.getDay();
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek].toLowerCase();
    const workingDaysRaw = availabilityRules.days || [];
    const workingDays = workingDaysRaw.map(d => (typeof d === 'string' ? d.toLowerCase() : d));

    if (!workingDays.includes(dayName)) {
      // Show 8 AM - 6 PM even on non-working days to indicate they're closed
      const granularity = parseInt(calendarConfig?.general?.granularity) || 30;
      const slots = [];
      for (let hour = 8; hour < 18; hour++) {
        for (let min = 0; min < 60; min += granularity) {
          slots.push(`${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
        }
      }
      return slots;
    }

    const granularity = parseInt(calendarConfig.general.granularity) || 30;
    const hoursStart = availabilityRules.hours_start || '09:00';
    const hoursEnd = availabilityRules.hours_end || '17:00';
    
    const [startHour, startMin] = hoursStart.split(':').map(Number);
    const [endHour, endMin] = hoursEnd.split(':').map(Number);

    // Show from 8 AM to 6 PM to include context around business hours
    const displayStartHour = 8;
    const displayEndHour = 18;
    const displayStartMinutes = displayStartHour * 60;
    const displayEndMinutes = displayEndHour * 60;

    const slots = [];
    for (let minutes = displayStartMinutes; minutes < displayEndMinutes; minutes += granularity) {
      const hour = Math.floor(minutes / 60);
      const min = minutes % 60;
      slots.push(`${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
    }

    return slots;
  };

  // Check if a time slot is within business hours
  const isWithinBusinessHours = (slotTime) => {
    if (!calendarConfig || !calendarConfig.business_hours || !calendarConfig.business_hours.availability_rules) {
      // Default: Monday-Friday 9-5, weekends closed
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false; // Sunday or Saturday - closed by default
      }
      
      const [hour, minute] = slotTime.split(':').map(Number);
      const slotMinutes = hour * 60 + minute;
      return slotMinutes >= 9 * 60 && slotMinutes < 17 * 60; // 9 AM - 5 PM
    }

    const dayOfWeek = currentDate.getDay();
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek].toLowerCase();
    const workingDaysRaw = calendarConfig.business_hours.availability_rules.days || [];
    const workingDays = workingDaysRaw.map(d => (typeof d === 'string' ? d.toLowerCase() : d));

    if (!workingDays.includes(dayName)) {
      return false; // Not a working day
    }

    const [hour, minute] = slotTime.split(':').map(Number);
    const slotMinutes = hour * 60 + minute;

    const hoursStart = calendarConfig.business_hours.availability_rules.hours_start || '09:00';
    const hoursEnd = calendarConfig.business_hours.availability_rules.hours_end || '17:00';

    const [startHour, startMin] = hoursStart.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;

    const [endHour, endMin] = hoursEnd.split(':').map(Number);
    const endMinutes = endHour * 60 + endMin;

    return slotMinutes >= startMinutes && slotMinutes < endMinutes;
  };

  // Get events for a specific time slot
  const getEventsForSlot = (slotTime) => {
    // IMPORTANT: Show events regardless of business hours
    // Events can be booked outside business hours via API
    // The slot itself will be marked as "closed" but events should still display
    
    const [hour, minute] = slotTime.split(':').map(Number);
    const slotStart = setMinutes(setHours(startOfDay(currentDate), hour), minute);
    const granularity = parseInt(calendarConfig?.general?.granularity) || 30;
    const slotEnd = new Date(slotStart.getTime() + granularity * 60000);

    return events.filter(event => {
      // Event overlaps with slot if:
      // 1. Event starts during this slot (event.start >= slotStart && event.start < slotEnd)
      // 2. Slot starts during the event (slotStart >= event.start && slotStart < event.end)
      return (event.start >= slotStart && event.start < slotEnd) ||
             (slotStart >= event.start && slotStart < event.end);
    });
  };

  // Check if event starts in this specific slot
  const eventStartsInSlot = (event, slotTime) => {
    const [hour, minute] = slotTime.split(':').map(Number);
    const slotStart = setMinutes(setHours(startOfDay(currentDate), hour), minute);
    const granularity = parseInt(calendarConfig?.granularity) || 30;
    const slotEnd = new Date(slotStart.getTime() + granularity * 60000);
    
    return event.start >= slotStart && event.start < slotEnd;
  };

  // Calculate how many 60px slots this event spans
  const calculateEventHeight = (event) => {
    const granularity = parseInt(calendarConfig?.granularity) || 30;
    const durationMinutes = (event.end - event.start) / 60000;
    const slots = Math.ceil(durationMinutes / granularity);
    const slotHeight = 60; // Each slot is 60px
    const gapBetweenSlots = 4; // mb: 0.5 = 0.5 * 8px = 4px
    // Calculate total height: (slots * slotHeight) + ((slots - 1) * gap) - small adjustment for padding
    return (slots * slotHeight) + ((slots - 1) * gapBetweenSlots) - 8; // -8px to account for padding inside the slot
  };

  const handleMeetingUpdated = () => {
    // Refresh events after booking/updating/canceling
    const fetchEvents = async () => {
      // Don't fetch if config isn't loaded yet
      if (!calendarConfig) {
        return;
      }
      
      try {
        setLoading(true);
        const customersData = await calendarService.getDayEvents(customerId, currentDate);
        
        const allEvents = [];
        // Parse events from nested structure
        Object.entries(customersData).forEach(([custId, customerInfo]) => {
          const calendars = customerInfo.calendars || {};
          Object.entries(calendars).forEach(([calendarName, data]) => {
            if (activeCalendar === calendarName) {
              const calendarEvents = data.calendar_events || [];
              calendarEvents.forEach(event => {
                const parsedEvent = calendarService.parseEvent(event);
                if (parsedEvent) {
                  allEvents.push({
                    ...parsedEvent,
                    calendarName,
                    customerId: custId,
                    businessName: customerInfo.business_name,
                  });
                }
              });
            }
          });
        });

        allEvents.sort((a, b) => a.start - b.start);
        setEvents(allEvents);
      } catch (err) {
        console.error('Error refreshing events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  };

  // Drag and drop handlers
  const handleDragStart = (event, meeting) => {
    event.stopPropagation();
    setDraggedEvent(meeting);
  };

  const handleDragOver = (event, slotTime) => {
    event.preventDefault();
    if (draggedEvent) {
      setDropTarget(slotTime);
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = (event, slotTime) => {
    event.preventDefault();
    setDropTarget(null);
    
    if (!draggedEvent || !slotTime) return;

    const [hour, minute] = slotTime.split(':').map(Number);
    const newStartTime = setMinutes(setHours(currentDate, hour), minute);
    
    // Check if the new slot is bookable
    const { isBookable } = isSlotBookable(newStartTime, calendarConfig, t);
    if (!isBookable) {
      alert(t('booking.slotNotAvailableForBooking'));
      setDraggedEvent(null);
      return;
    }

    // Open rebook dialog with pre-filled date/time
    setSelectedMeeting(draggedEvent);
    setPrefilledDate(newStartTime);
    setShowBookDialog(true);
    setDraggedEvent(null);
  };

  return (
    <Box>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <IconButton onClick={handlePreviousDay}>
            <ChevronLeftIcon />
          </IconButton>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" component="div">
              {format(currentDate, 'EEEE', { locale: dateLocale })}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {format(currentDate, 'MMMM d, yyyy', { locale: dateLocale })}
            </Typography>
          </Box>

          <IconButton onClick={handleNextDay}>
            <ChevronRightIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={isToday(currentDate) ? 'contained' : 'outlined'}
            startIcon={<TodayIcon />}
            onClick={handleToday}
            fullWidth
          >
            {t('common.today')}
          </Button>

          {calendars.length > 1 && (
            <FormControl fullWidth>
              <InputLabel>Calendar</InputLabel>
              <Select
                value={activeCalendar}
                label="Calendar"
                onChange={(e) => setActiveCalendar(e.target.value)}
              >
                {calendars.map((cal) => (
                  <MenuItem key={cal.name} value={cal.name}>
                    {cal.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Paper>

      {/* Time Grid View */}
      <Box sx={{ pb: 10, position: 'relative' }}>
        {/* Loading Overlay */}
        {loading && (
          <Backdrop
            open={true}
            sx={{
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              bgcolor: (theme) => theme.palette.mode === 'dark' 
                ? 'rgba(0, 0, 0, 0.7)' 
                : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <CircularProgress />
          </Backdrop>
        )}
        
        {!calendarConfig && !loading ? (
          <LoadingSpinner />
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : (
          <>
            {generateTimeSlots().length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No working hours configured for this day
                </Typography>
              </Paper>
            ) : (
              <Box>
                {generateTimeSlots().map((slotTime, index) => {
                  const slotEvents = getEventsForSlot(slotTime);
                  const [hour, minute] = slotTime.split(':').map(Number);
                  const displayTime = format(setMinutes(setHours(new Date(), hour), minute), 'h:mm a');
                  const isOffHours = !isWithinBusinessHours(slotTime);
                  
                  // Validate slot bookability
                  const slotDateTime = setMinutes(setHours(currentDate, hour), minute);
                  const { isBookable, reason } = isSlotBookable(slotDateTime, calendarConfig, t);
                  
                  // Check if this slot has an event starting here
                  const hasEventStartingHere = slotEvents.some(event => eventStartsInSlot(event, slotTime));
                  // Check if this slot is covered by any event (for blue border)
                  const hasCoveringEvent = slotEvents.length > 0;
                  // Check if slot is covered by event but event doesn't start here
                  const isCoveredByEventAbove = hasCoveringEvent && !hasEventStartingHere;
                  
                  return (
                    <Paper 
                      key={slotTime} 
                      elevation={isCoveredByEventAbove ? 0 : 1}
                      title={!isBookable ? reason : ''}
                      sx={{ 
                        mb: 0.5,
                        position: 'relative',
                        overflow: 'visible',
                        borderLeft: 4,
                        borderColor: hasCoveringEvent ? 'primary.main' : 'transparent',
                        bgcolor: isCoveredByEventAbove 
                          ? 'transparent'
                          : isOffHours 
                          ? (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'
                          : !isBookable
                          ? (theme) => theme.palette.mode === 'dark' ? 'grey.850' : 'grey.200'
                          : (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper',
                        opacity: !isBookable && !isCoveredByEventAbove ? 0.5 : 1,
                        cursor: isBookable && !isOffHours ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: isBookable && !isOffHours ? 3 : 1,
                        },
                        // Drop target styling
                        ...(dropTarget === slotTime && isBookable && !isOffHours && {
                          bgcolor: 'primary.light',
                          opacity: 0.3,
                        })
                      }}
                      onDragOver={(e) => handleDragOver(e, slotTime)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, slotTime)}
                    >
                      <Box sx={{ display: 'flex', height: 60 }}>
                        {/* Time Label */}
                        <Box 
                          sx={{ 
                            width: 80, 
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRight: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" fontWeight="medium">
                            {displayTime}
                          </Typography>
                        </Box>

                        {/* Events Container */}
                        <Box sx={{ flex: 1, p: 1, position: 'relative', overflow: 'visible' }}>
                          {slotEvents.length === 0 ? (
                            <Box 
                              sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.disabled',
                                fontSize: '0.75rem'
                              }}
                            >
                              {isOffHours ? t('booking.closed') : !isBookable ? t('booking.unavailable') : t('booking.available')}
                            </Box>
                          ) : null}
                          
                          {/* Render events that start in this slot as overlays */}
                          {slotEvents
                            .filter(event => eventStartsInSlot(event, slotTime))
                            .map((event) => {
                              const eventHeight = calculateEventHeight(event);
                              const isConfirmed = event.confirmationStatus === 'confirmed';
                              const isPending = event.confirmationStatus === 'pending';
                              const typeLabel = event.typeDescription || event.type || event.summary || '';
                              const patientName = event.customerName || '';
                              const timeRange = `${format(event.start, 'h:mm a')} - ${format(event.end, 'h:mm a')}`;
                              const secondLine = patientName ? `${timeRange} ${patientName}` : timeRange;
                              
                              return (
                                <Tooltip
                                  key={event.id}
                                  title={
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {typeLabel || t('booking.available')}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {secondLine}
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="left"
                                >
                                  <Box
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, event)}
                                    onClick={() => handleMeetingClick(event)}
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      left: 4,
                                      right: 4,
                                      height: `${eventHeight}px`,
                                      zIndex: 10,
                                      p: 2,
                                      bgcolor: !event.isCustomerMeeting
                                        ? 'grey.200'
                                        : isConfirmed
                                          ? 'success.light'
                                          : isPending
                                            ? 'warning.light'
                                            : 'primary.light',
                                      borderLeft: isConfirmed ? 4 : 2,
                                      borderColor: isConfirmed
                                        ? 'success.main'
                                        : event.isCustomerMeeting
                                          ? 'primary.main'
                                          : 'grey.400',
                                      borderStyle: 'solid',
                                      borderRadius: 1,
                                      cursor: 'move',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      boxShadow: 2,
                                      overflow: 'hidden',
                                      '&:hover': {
                                        bgcolor: !event.isCustomerMeeting
                                          ? 'grey.300'
                                          : isConfirmed
                                            ? 'success.main'
                                            : isPending
                                              ? 'warning.main'
                                              : 'primary.main',
                                        boxShadow: 4,
                                      },
                                      opacity: draggedEvent?.id === event.id ? 0.5 : 1,
                                    }}
                                  >
                                    {isConfirmed && (
                                      <CheckCircleIcon
                                        sx={{
                                          position: 'absolute',
                                          top: 4,
                                          right: 4,
                                          fontSize: '0.9rem',
                                          color: 'success.main',
                                          bgcolor: 'white',
                                          borderRadius: '50%',
                                        }}
                                      />
                                    )}
                                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                                      {typeLabel || t('booking.available')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {secondLine}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add meeting"
        onClick={handleAddMeeting}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
      >
        <AddIcon />
      </Fab>

      {/* Dialogs */}
      <BookMeetingDialog
        open={showBookDialog}
        onClose={(success) => {
          setShowBookDialog(false);
          setSelectedMeeting(null);
          setPrefilledDate(null);
          if (success) {
            handleMeetingUpdated();
          }
        }}
        customerId={customerId}
        calendarName={activeCalendar !== 'all' ? activeCalendar : null}
        prefilledDate={prefilledDate || currentDate}
        existingMeeting={selectedMeeting}
        isRescheduling={!!selectedMeeting}
      />

      <MeetingDetailsDialog
        open={showDetailsDialog}
        onClose={() => {
          setShowDetailsDialog(false);
          setSelectedMeeting(null);
        }}
        meeting={selectedMeeting}
        customerId={customerId}
        onMeetingUpdated={handleMeetingUpdated}
      />
    </Box>
  );
};

export default DailyCalendar;
